import React, { useEffect, useRef, useState } from "react";
import ContentCard from "../../../components/misc/ContentCard";
import TextInput from "../../../components/misc/TextInput";
import CustomButton from "../../../components/CustomButton";
import ActionMenu from "../../../components/actionMenu/ActionMenu";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  fetchDownloadHistory4GpsReport,
  fetchDownloadHistoryTemperatureRaport,
  fetchPackageHistory,
  fetchPackageHistoryWithActions,
} from "../../../redux/api/raports.api";
import { getDateAndTime } from "../../../utils/dateAndTime";
import {
  clearPackageHistory,
  clearPackageHistoryWithActions,
} from "../../../redux/reducer/raports.reducer";
import LoadingIndicator from "../../../components/misc/LoadingIndicator";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import userRoles from "../../../constants/userRoles";
import usePermissions from "../../../router/usePermissions";

const getPackageHistoryType = (item: any) => {
  const type = item.tip;
  switch (type) {
    case "creat":
      return (
        <p>
          Coletul a fost pregatit de clientul{" "}
          <span className="font-bold">{item.denumire}</span> cu ridicare de la{" "}
          <span className="font-bold">{item.preluare}</span> in data de{" "}
          <span className="font-bold">
            {getDateAndTime(item.data_incarcare)}
          </span>
        </p>
      );

    case "auto":
      return (
        <p>
          Coletul a fost incarcat in masina{" "}
          <span className="font-bold">{item.denumire}</span> de catre{" "}
          <span className="font-bold">
            {item.nume} {item.prenume}
          </span>{" "}
          la data de{" "}
          <span className="font-bold">
            {getDateAndTime(item.data_incarcare)}
          </span>
        </p>
      );

    case "nod":
      return (
        <p>
          {item.id_bin ? "Coletul a intrat" : "Coletul a fost scanat"} in nodul{" "}
          <span className="font-bold">{item.denumire}</span> la data de{" "}
          <span className="font-bold">
            {getDateAndTime(item.data_incarcare)}
          </span>
        </p>
      );

    case "livrat":
      return (
        <p>
          Coletul a fost livrat la{" "}
          <span className="font-bold">{item.denumire}</span> la data de{" "}
          <span className="font-bold">
            {getDateAndTime(item.data_descarcare)}
          </span>{" "}
          {item.motiv && (
            <>
              cu motivul <span className="font-bold">"{item.motiv}"</span>
            </>
          )}
        </p>
      );

    default:
      return "Nu exista";
  }
};

const getPackageHistoryTypeWithActions = (item: any) => {
  const type = item.task_type;
  let statusDescription = "";
  switch (type) {
    case 1:
      statusDescription = "Creeaza trasabilitate";
      break;

    case 2:
      statusDescription = "Incarca colet in masina";
      break;

    case 3:
      statusDescription = "Schimbare data ridicare";
      break;

    case 4:
      statusDescription = "Finalizare livrare";
      break;

    case 5:
      statusDescription = "Anulare awb";
      break;

    default:
      return "Nu exista";
  }

  return (
    <p>
      <span className="font-bold">
        {item.nume} {item.prenume}
      </span>{" "}
      a apasat butonul <span className="font-bold">"{statusDescription}" </span>
      la data de
      <span className="font-bold"> {getDateAndTime(item.ts_operatie)}</span>
    </p>
  );
};

function PackageHistory() {
  const dispatch = useAppDispatch();
  const { isAuthorized } = usePermissions();
  const canDownload4GpsData = isAuthorized(userRoles.DOWNLOAD_4GPS_DATA);

  const {
    raports: {
      packageHistory: {
        data: packageHistory,
        isLoading: isLoadingPackageHistory,
      },
      packageHistoryWithActions: {
        data: packageHistoryWithActions,
        isLoading: isLoadingPackageHistoryWithActions,
      },
    },
  } = useAppSelector((state) => ({
    raports: state.raports,
  }));

  const [awbOrInvoiceNumber, setAwbOrInvoiceNumber] = useState<
    string | number | null
  >(null);
  const [selectedAwb, setSelectedAwb] = useState<DropdownValue | null>(null);
  const [downloadInProgress, setDownloadInProgress] = useState<
    "temperature" | "4gps" | null
  >(null);
  const isDownloading = useRef(false);

  const handleSearchPackageHistoryByAwbOrInvoiceNumber = () => {
    if (awbOrInvoiceNumber) {
      dispatch(
        fetchPackageHistory({
          doc_trans: awbOrInvoiceNumber,
        }),
      );
      dispatch(
        fetchPackageHistoryWithActions({
          doc_trans: awbOrInvoiceNumber,
        }),
      );
    }
  };

  useEffect(() => {
    if (packageHistory.length > 0) {
      setSelectedAwb({
        id: packageHistory[0].awb,
        label: packageHistory[0].awb,
      });
    }
  }, [packageHistory]);

  useEffect(() => {
    return () => {
      dispatch(clearPackageHistory());
      dispatch(clearPackageHistoryWithActions());
    };
  }, [dispatch]);

  const showDownloadButton =
    packageHistory.length > 0 && !isLoadingPackageHistory;

  const handleDownloadTemperatureReport = async () => {
    if (selectedAwb?.id && !isDownloading.current) {
      isDownloading.current = true;
      setDownloadInProgress("temperature");
      try {
        await dispatch(
          fetchDownloadHistoryTemperatureRaport({
            awb: selectedAwb?.id,
          }),
        );
      } finally {
        isDownloading.current = false;
        setDownloadInProgress(null);
      }
    }
  };

  const handleDownload4GpsReport = async () => {
    if (canDownload4GpsData && selectedAwb?.id && !isDownloading.current) {
      isDownloading.current = true;
      setDownloadInProgress("4gps");
      try {
        await dispatch(
          fetchDownloadHistory4GpsReport({
            awb: selectedAwb.id,
          }),
        );
      } finally {
        isDownloading.current = false;
        setDownloadInProgress(null);
      }
    }
  };

  const selectedPackageHistory =
    packageHistory?.find((item) => item.awb === selectedAwb?.id)?.actiuni ?? [];
  const selectedPackageHistoryWithActions =
    packageHistoryWithActions?.find((item) => item.awb === selectedAwb?.id)
      ?.actiuni ?? [];

  const awbList = packageHistory.map((item) => ({
    id: item.awb,
    label: item.awb,
  }));

  return (
    <div>
      <ContentCard cardTitle="Cauta istoricul unui colet dupa awb sau nr. factura">
        <div className="flex items-center gap-2">
          <TextInput
            type="text"
            placeholder="Introduceti awb-ul sau nr. facturii"
            name="awbOrInvoiceNumber"
            value={awbOrInvoiceNumber}
            onChange={(value) => setAwbOrInvoiceNumber(value)}
            withVerticalSpacer
            withUpperCasing
          />

          <CustomButton
            variant="contained"
            className="mb-7 h-[50px] min-w-[100px]"
            onClick={handleSearchPackageHistoryByAwbOrInvoiceNumber}>
            Cauta
          </CustomButton>
        </div>
        {awbList.length > 0 && (
          <Dropdown
            title="Cauta AWB"
            className=""
            data={awbList}
            singleValue={selectedAwb}
            setSingleValue={setSelectedAwb}
          />
        )}
      </ContentCard>

      {isLoadingPackageHistory && (
        <div className="flex items-center justify-center">
          <LoadingIndicator />
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row">
        {selectedPackageHistory.length > 0 && !isLoadingPackageHistory && (
          <ContentCard
            cardTitle="Istoricul coletului"
            fullWidth
            cardContainerStyle={{ minWidth: 400 }}>
            {selectedPackageHistory.map((item, idx) => {
              return (
                <ContentCard
                  key={idx}
                  withPadding={false}
                  withMargin={false}
                  className="my-5 px-5 py-2">
                  <div className="flex flex-row items-center pt-3">
                    <p className="mr-2">{idx + 1}.</p>
                    {getPackageHistoryType(item)}
                  </div>
                </ContentCard>
              );
            })}
          </ContentCard>
        )}

        {selectedPackageHistoryWithActions.length > 0 &&
          !isLoadingPackageHistoryWithActions && (
            <ContentCard
              cardTitle="Actiuni pe colet"
              fullWidth
              cardContainerStyle={{ minWidth: 400 }}>
              {selectedPackageHistoryWithActions.map((item, idx) => {
                return (
                  <ContentCard
                    key={idx}
                    withPadding={false}
                    withMargin={false}
                    className="my-5 px-5 py-2">
                    <div className="flex flex-row items-center pt-3">
                      <p className="mr-2">{idx + 1}.</p>
                      {getPackageHistoryTypeWithActions(item)}
                    </div>
                  </ContentCard>
                );
              })}
            </ContentCard>
          )}
      </div>

      {showDownloadButton && (
        <div className="flex content-center justify-center gap-2">
          <ActionMenu
            title={selectedAwb?.label ?? undefined}
            minItemsForMenu={2}
            items={[
              {
                id: "download-temperature-history",
                label: "Descarca diagrama temperatura",
                icon: "TEMPERATURE",
                kind: "download",
                tooltip: selectedAwb?.id
                  ? "Descarca diagrama de temperatura pentru AWB-ul selectat."
                  : "Selecteaza un AWB pentru a descarca diagrama de temperatura.",
                disabled: !selectedAwb?.id,
                loading: downloadInProgress === "temperature",
                onClick: handleDownloadTemperatureReport,
              },
              {
                id: "download-4gps-history",
                label: "Descarca date 4GPS",
                icon: "GPS_DATA",
                kind: "download",
                tooltip: selectedAwb?.id
                  ? "Descarca datele 4GPS pentru AWB-ul selectat."
                  : "Selecteaza un AWB pentru a descarca datele 4GPS.",
                hidden: !canDownload4GpsData,
                disabled: !selectedAwb?.id,
                loading: downloadInProgress === "4gps",
                onClick: handleDownload4GpsReport,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

export default PackageHistory;
